import { Abstract } from '@/module/Author/Abstract';
import { Article } from '@/module/Author/Article';
import { Award } from '@/module/Author/Award';
import { Book } from '@/module/Author/Book';
import { Chapter } from '@/module/Author/Chapter';
import { Citation } from '@/module/Author/Citation/model/CitationTypes';
import { Committee } from '@/module/Author/Committee';
import { Course } from '@/module/Author/Course';
import { Patent } from '@/module/Author/Patent';
import { Proceeding } from '@/module/Author/Proceeding';
import { Project } from '@/module/Author/Project';
import { Translation } from '@/module/Author/Translation';

import { BasicUser, JobTitle, PersistentFile } from '@feature/utils';

export interface MinStdReport {
  id: number;
  user: BasicUser;
  minStdAnnexNo: number;
  implemented: boolean;
}

export interface Indicator {
  indicatorCode: string;
  formattedIndicatorCode: string;
  title: string;
  legend: string;

  splitByYear: boolean;
  autoAddAll: boolean;

  indicatorType: MinStdIndicatorType;
  manualPoints: boolean;
  category: string | null;
}

export interface MinStdAnnexTemplate {
  annexNo: number;
  title: string;
  indicators: Indicator[];
}

export interface MinStdRequest {
  minStdAnnexNo: number;
}

export interface MinStdAvailableAnnex {
  annexNo: number;
  title: string;
}

export interface IndicatorResult {
  indicator: Indicator;
  totalScore: number;

  items: IndicatorItem[];
}

export interface Criteria {
  title: string;
  criteriaCode: string;
  minimumPoints: {
    map: Record<JobTitle, string>;
  };
}

export interface CriteriaResult {
  criteria: Criteria;
  minimumPointsForGivenJobTitle: string;
  totalScore: number;
  passed: boolean;
}

export interface MinStdResultReport {
  annexNo: number;
  title: string;

  user: BasicUser;

  jobTitle: JobTitle;
  startYear: number;
  endYear: number;

  indicators: IndicatorResult[];
  criteria: CriteriaResult[];

  totalScore: number;
  unfulfilledCriteria: string[];
}

export type MinStdIndicatorType =
  | 'FREE_ANSWER'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'INTERVAL'
  | 'SELECT_ABSTRACT'
  | 'SELECT_ARTICLE'
  | 'SELECT_BOOK'
  | 'SELECT_CHAPTER'
  | 'SELECT_COURSE'
  | 'SELECT_COMMITTEE'
  | 'SELECT_PROJECT'
  | 'SELECT_PATENT'
  | 'SELECT_TRANSLATION'
  | 'SELECT_PROCEEDING'
  | 'SELECT_AWARD'
  | 'SELECT_CITATION';

interface BaseIndicatorItem {
  id: number;
  note: string;
  link: string | null;
  file: PersistentFile | null;
  points: number;

  context: Record<string, string> | null;

  indicatorType: MinStdIndicatorType;
}

export type IndicatorItem =
  | MinStdIndicatorFreeItem
  | MinStdIndicatorBooleanItem
  | MinStdIndicatorNumberItem
  | MinStdIndicatorIntervalItem
  | MinStdIndicatorAwardItem
  | MinStdIndicatorCourseItem
  | MinStdIndicatorAbstractItem
  | MinStdIndicatorArticleItem
  | MinStdIndicatorBookItem
  | MinStdIndicatorChapterItem
  | MinStdIndicatorCommitteeItem
  | MinStdIndicatorPatentItem
  | MinStdIndicatorProjectItem
  | MinStdIndicatorTranslationItem
  | MinStdIndicatorProceedingItem
  | MinStdIndicatorCitationItem;

export function isMinStdIndicatorFreeItem(item: IndicatorItem): item is MinStdIndicatorFreeItem {
  return item.indicatorType === 'FREE_ANSWER';
}

export function isMinStdIndicatorBooleanItem(item: IndicatorItem): item is MinStdIndicatorBooleanItem {
  return item.indicatorType === 'BOOLEAN';
}

export function isMinStdIndicatorNumberItem(item: IndicatorItem): item is MinStdIndicatorNumberItem {
  return item.indicatorType === 'NUMBER';
}

export function isMinStdIndicatorIntervalItem(item: IndicatorItem): item is MinStdIndicatorIntervalItem {
  return item.indicatorType === 'INTERVAL';
}

export function isMinStdIndicatorAbstractItem(item: IndicatorItem): item is MinStdIndicatorAbstractItem {
  return item.indicatorType === 'SELECT_ABSTRACT';
}

export function isMinStdIndicatorArticleItem(item: IndicatorItem): item is MinStdIndicatorArticleItem {
  return item.indicatorType === 'SELECT_ARTICLE';
}

export function isMinStdIndicatorBookItem(item: IndicatorItem): item is MinStdIndicatorBookItem {
  return item.indicatorType === 'SELECT_BOOK';
}

export function isMinStdIndicatorTranslationItem(item: IndicatorItem): item is MinStdIndicatorTranslationItem {
  return item.indicatorType === 'SELECT_TRANSLATION';
}

export function isMinStdIndicatorChapterItem(item: IndicatorItem): item is MinStdIndicatorChapterItem {
  return item.indicatorType === 'SELECT_CHAPTER';
}

export function isMinStdIndicatorCommitteeItem(item: IndicatorItem): item is MinStdIndicatorCommitteeItem {
  return item.indicatorType === 'SELECT_COMMITTEE';
}

export function isMinStdIndicatorCourseItem(item: IndicatorItem): item is MinStdIndicatorCourseItem {
  return item.indicatorType === 'SELECT_COURSE';
}

export function isMinStdIndicatorProjectItem(item: IndicatorItem): item is MinStdIndicatorProjectItem {
  return item.indicatorType === 'SELECT_PROJECT';
}

export function isMinStdIndicatorPatentItem(item: IndicatorItem): item is MinStdIndicatorPatentItem {
  return item.indicatorType === 'SELECT_PATENT';
}

export function isMinStdIndicatorProceedingItem(item: IndicatorItem): item is MinStdIndicatorProceedingItem {
  return item.indicatorType === 'SELECT_PROCEEDING';
}

export function isMinStdIndicatorAwardItem(item: IndicatorItem): item is MinStdIndicatorAwardItem {
  return item.indicatorType === 'SELECT_AWARD';
}

export function isMinStdIndicatorCitationItem(item: IndicatorItem): item is MinStdIndicatorCitationItem {
  return item.indicatorType === 'SELECT_CITATION';
}

export interface MinStdIndicatorFreeItem extends BaseIndicatorItem {
  indicatorType: 'FREE_ANSWER';
  activityDescription: string;
}

export interface MinStdInterval {
  activity: string;
  startYear: number;
  endYear: number | null;
}

export interface MinStdIndicatorBooleanItem extends BaseIndicatorItem {
  indicatorType: 'BOOLEAN';
}

export interface MinStdIndicatorIntervalItem extends BaseIndicatorItem {
  indicatorType: 'INTERVAL';
  interval: MinStdInterval;
}

export interface MinStdIndicatorNumberItem extends BaseIndicatorItem {
  indicatorType: 'NUMBER';
  number: number;
}

export interface MinStdIndicatorAwardItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_AWARD';
  award: Award;
}

export interface MinStdIndicatorCitationItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_CITATION';
  citation: Citation;
}

export interface MinStdIndicatorAbstractItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_ABSTRACT';
  abstractItem: Abstract;
}

export interface MinStdIndicatorArticleItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_ARTICLE';
  article: Article;
}

export interface MinStdIndicatorBookItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_BOOK';
  book: Book;
}

export interface MinStdIndicatorChapterItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_CHAPTER';
  chapter: Chapter;
}

export interface MinStdIndicatorProceedingItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_PROCEEDING';
  proceeding: Proceeding;
}

export interface MinStdIndicatorTranslationItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_TRANSLATION';
  translation: Translation;
}

export interface MinStdIndicatorCourseItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_COURSE';
  course: Course;
}

export interface MinStdIndicatorCommitteeItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_COMMITTEE';
  committee: Committee;
}

export interface MinStdIndicatorProjectItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_PROJECT';
  project: Project;
}

export interface MinStdIndicatorPatentItem extends BaseIndicatorItem {
  indicatorType: 'SELECT_PATENT';
  patent: Patent;
}

export interface IndicatorItemRequest {
  note?: string | null;
  link: string | null;

  newFile: File | null;

  year: number | null;

  points?: number | null;
  indicatorType: MinStdIndicatorType;
}

export interface IndicatorItemOption<T> {
  option: T;

  context: Record<string, string> | null;

  selectable: boolean;
  mismatchedConditionType: string | null;
}
